import { useState } from "react";
import { useNavigate } from "react-router-dom";

const programs = [
  {
    id: "stress",
    label: "Stress Resilience",
    icon: "ri-mental-health-line",
    color: "#00a882",
    desc: "Employees receive personalized Apollo wearable protocols designed to reduce workplace stress, lower cortisol, and build long-term nervous system resilience — measurable from day one.",
    benefit: "19% avg. cortisol reduction",
  },
  {
    id: "focus",
    label: "Peak Performance",
    icon: "ri-brain-line",
    color: "#6366f1",
    desc: "Targeted vibration frequencies entrain brainwave activity toward deep focus states, helping teams sustain attention during high-demand work periods without stimulants.",
    benefit: "28% improvement in sustained focus",
  },
  {
    id: "sleep",
    label: "Recovery & Sleep",
    icon: "ri-moon-line",
    color: "#00d4a8",
    desc: "Guided sleep protocols help employees recover faster, reduce burnout risk, and arrive at work fully restored — directly impacting productivity and retention.",
    benefit: "37-min avg. sleep onset improvement",
  },
  {
    id: "energy",
    label: "Energy Management",
    icon: "ri-flashlight-line",
    color: "#4f46e5",
    desc: "Morning activation modes prime the nervous system for high-output days. Afternoon recovery modes prevent the 2pm slump — keeping teams energized across full workdays.",
    benefit: "40% increase in reported alertness",
  },
];

const outcomes = [
  { icon: "ri-arrow-down-line", value: "32%", label: "Reduction in Sick Days" },
  { icon: "ri-arrow-up-line", value: "41%", label: "Increase in Productivity" },
  { icon: "ri-heart-pulse-line", value: "2,400+", label: "Employees Enrolled" },
  { icon: "ri-trophy-line", value: "94%", label: "Program Satisfaction" },
];

export default function WellnessSection() {
  const [activeProgram, setActiveProgram] = useState(0);
  const navigate = useNavigate();
  const current = programs[activeProgram];

  return (
    <section id="wellness-wearables" className="bg-[#f4faf7] py-28 relative overflow-hidden">
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "radial-gradient(circle, #00a88220 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 items-start">
          <div className="text-center">
            <div className="text-2xl md:text-3xl lg:text-4xl font-black tracking-widest uppercase bg-clip-text text-transparent bg-gradient-to-r from-[#00c896] via-[#00ffcc] to-[#00a882] mb-4 leading-none">
              Apollo Neuro
            </div>
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-black text-[#0a2a1f] leading-none tracking-tight">
              Business Wellness
            </h2>
            <div className="text-5xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#00a882] to-[#00d4a8] mt-3 leading-none">
              Program
            </div>
          </div>
          <div className="flex items-start justify-center lg:pt-2">
            <p className="text-[#0a2a1f]/80 text-xl md:text-2xl leading-relaxed font-light text-center max-w-sm">
              MDLARK brings Apollo Neuro's clinically validated wearable technology to the enterprise — measurably reducing stress, boosting performance, and transforming workplace culture.
            </p>
          </div>
        </div>

        {/* Research Highlight Banner */}
        <div className="w-full rounded-2xl border border-[#00a882]/25 bg-white overflow-hidden mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left accent */}
            <div className="lg:col-span-1 bg-gradient-to-br from-[#00a882] to-[#00d4a8] p-8 flex flex-col items-center justify-center text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/20 mb-3">
                <i className="ri-microscope-line text-white text-2xl"></i>
              </div>
              <div className="text-white font-black text-lg leading-tight">Peer-Reviewed</div>
              <div className="text-white/80 text-xs mt-1 tracking-wide uppercase">Research Finding</div>
            </div>
            {/* Right content */}
            <div className="lg:col-span-4 p-8 flex flex-col justify-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#00a882] animate-pulse"></div>
                <span className="text-[#00a882] text-xs font-bold tracking-[0.2em] uppercase">University of Pittsburgh — Clinical Study</span>
              </div>
              <p className="text-[#0a2a1f] font-bold text-xl md:text-2xl leading-snug">
                Apollo Neuro produces nervous system effects <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00a882] to-[#00d4a8]">as powerful as meditation</span> — without requiring any training, practice, or dedicated time.
              </p>
              <p className="text-[#0a2a1f]/65 text-sm leading-relaxed max-w-2xl">
                In a double-blind, placebo-controlled trial, participants using Apollo Neuro showed statistically significant improvements in HRV (heart rate variability), cortisol levels, and self-reported calm — results comparable to an experienced meditator's session. Unlike meditation, Apollo works passively in the background while employees stay fully productive.
              </p>
              <div className="flex flex-wrap gap-3 mt-1">
                {[
                  { icon: "ri-heart-pulse-line", text: "HRV improvement equivalent to 20-min meditation" },
                  { icon: "ri-test-tube-line", text: "Double-blind, placebo-controlled trial" },
                  { icon: "ri-time-line", text: "Zero time investment from employees" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00a882]/8 border border-[#00a882]/20">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} text-[#00a882] text-xs`}></i>
                    </div>
                    <span className="text-[#0a2a1f]/75 text-xs font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start -mt-6">

          {/* Left: Program details */}
          <div className="flex flex-col gap-6">
            <div>
              <h3 className="text-[#0a2a1f] font-bold text-xl mb-3">How the Program Works</h3>
              <p className="text-[#0a2a1f]/70 text-sm leading-relaxed">
                Each employee receives an Apollo Neuro wearable device paired with a customized wellness protocol. Our certified wellness coaches onboard your team, track biometric outcomes, and continuously optimize the program — delivering measurable ROI on employee health and performance.
              </p>
            </div>

            {/* Program tabs */}
            <div className="flex flex-wrap gap-2">
              {programs.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => setActiveProgram(i)}
                  className={`whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer border ${
                    activeProgram === i
                      ? "border-transparent text-white"
                      : "border-[#0a2a1f]/10 text-[#0a2a1f]/60 hover:border-[#00a882]/30 hover:text-[#0a2a1f] bg-white"
                  }`}
                  style={activeProgram === i ? { backgroundColor: p.color } : {}}
                >
                  <i className={`${p.icon} text-xs`}></i>
                  {p.label}
                </button>
              ))}
            </div>

            {/* Active program detail */}
            <div
              className="rounded-xl border p-6 transition-all duration-300 bg-white"
              style={{ borderColor: `${current.color}30` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-lg"
                  style={{ backgroundColor: `${current.color}15` }}
                >
                  <i className={`${current.icon} text-lg`} style={{ color: current.color }}></i>
                </div>
                <div className="text-[#0a2a1f] font-semibold">{current.label}</div>
              </div>
              <p className="text-[#0a2a1f]/75 text-sm leading-relaxed mb-4">{current.desc}</p>
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: `${current.color}15`, color: current.color }}
              >
                <i className="ri-flask-line text-xs"></i>
                {current.benefit}
              </div>
            </div>

            {/* What's included */}
            <div className="rounded-xl border border-[#0a2a1f]/10 bg-white p-6">
              <div className="text-[#0a2a1f] font-semibold text-sm mb-4">What&apos;s Included</div>
              <div className="flex flex-col gap-3">
                {[
                  { icon: "ri-device-line", text: "Apollo Neuro wearable device for every enrolled employee" },
                  { icon: "ri-user-star-line", text: "Dedicated wellness coach & onboarding support" },
                  { icon: "ri-bar-chart-line", text: "Monthly biometric reporting & ROI dashboard" },
                  { icon: "ri-refresh-line", text: "Continuous protocol optimization based on team data" },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#00a882]/10 flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#00a882] text-sm`}></i>
                    </div>
                    <span className="text-[#0a2a1f]/75 text-sm leading-relaxed">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Image + outcomes */}
          <div className="flex flex-col gap-6 items-center">

            {/* Square photo */}
            <div className="relative w-[320px] h-[320px] rounded-2xl overflow-hidden border border-[#0a2a1f]/10 flex-shrink-0 -mt-8">
              <img
                src="https://public.readdy.ai/ai/img_res/9b2d1b7e2f9f27e6f2f3097153785a91.jpg"
                alt="Apollo Business Wellness Program"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a2a1f]/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-3 left-4">
                <div className="text-white font-bold text-sm">Apollo Business Wellness</div>
              </div>
            </div>

            {/* All 6 stats centered under photo */}
            <div className="w-full grid grid-cols-3 gap-3">
              {[
                { icon: "ri-microscope-line", value: "40+", label: "Peer-Reviewed Studies" },
                { icon: "ri-group-line", value: "2,400+", label: "Clinical Trial Participants" },
                { icon: "ri-arrow-down-line", value: "32%", label: "Reduction in Sick Days" },
                { icon: "ri-arrow-up-line", value: "41%", label: "Increase in Productivity" },
                { icon: "ri-heart-pulse-line", value: "2,400+", label: "Employees Enrolled" },
                { icon: "ri-trophy-line", value: "94%", label: "Program Satisfaction" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-[#00a882]/20 bg-white p-4 text-center hover:border-[#00a882]/40 transition-all duration-300">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#00a882]/10 mx-auto mb-2">
                    <i className={`${s.icon} text-[#00a882] text-sm`}></i>
                  </div>
                  <div className="text-[#00a882] font-black text-xl">{s.value}</div>
                  <div className="text-[#0a2a1f]/65 text-xs mt-1 leading-tight">{s.label}</div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="w-full rounded-xl border border-[#00a882]/20 bg-[#00a882]/5 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <div className="text-[#0a2a1f] font-bold text-base mb-1">Ready to transform your workforce?</div>
                <div className="text-[#0a2a1f]/60 text-sm">Custom programs for teams of 25 to 25,000+</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <button className="whitespace-nowrap px-6 py-3 rounded-full bg-[#00a882] text-white font-semibold text-sm hover:bg-[#008f6e] transition-colors cursor-pointer">
                  Get a Quote
                </button>
                <button
                  onClick={() => navigate("/apollo-neuro")}
                  className="whitespace-nowrap px-6 py-3 rounded-full border-2 border-[#00a882] text-[#00a882] font-semibold text-sm hover:bg-[#00a882]/10 transition-colors cursor-pointer flex items-center gap-2"
                >
                  Visit Apollo Neuro
                  <i className="ri-arrow-right-line text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            {
              name: "Jennifer Marsh",
              role: "Chief People Officer, Apex Corp",
              quote: "We rolled out the Apollo Business Wellness Program to 400 employees. Sick day usage dropped 29% in the first quarter alone. The ROI was undeniable.",
              avatar: "https://readdy.ai/api/search-image?query=professional%20female%20executive%20headshot%2C%20confident%20warm%20expression%2C%20modern%20office%20background%2C%20clean%20corporate%20portrait%20photography&width=80&height=80&seq=biz_avatar001&orientation=squarish",
            },
            {
              name: "David Okafor",
              role: "VP of Operations, NovaTech",
              quote: "Our teams work in high-pressure environments. Apollo's stress resilience protocols have been a game changer — focus is up, burnout is down.",
              avatar: "https://readdy.ai/api/search-image?query=professional%20male%20executive%20headshot%2C%20confident%20expression%2C%20modern%20office%20background%2C%20clean%20corporate%20portrait%20photography&width=80&height=80&seq=biz_avatar002&orientation=squarish",
            },
            {
              name: "Dr. Lisa Tran",
              role: "Corporate Health Director, MedGroup",
              quote: "The biometric reporting dashboard gives us real data on employee wellbeing. We can actually see the nervous system improvements month over month.",
              avatar: "https://readdy.ai/api/search-image?query=professional%20female%20doctor%20executive%20headshot%2C%20confident%20expression%2C%20medical%20corporate%20office%20background%2C%20clean%20portrait%20photography&width=80&height=80&seq=biz_avatar003&orientation=squarish",
            },
          ].map((t) => (
            <div key={t.name} className="rounded-xl border border-[#0a2a1f]/10 bg-white p-6 hover:border-[#00a882]/40 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-[#0a2a1f]/10">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-[#0a2a1f] font-semibold text-sm">{t.name}</div>
                  <div className="text-[#0a2a1f]/50 text-xs">{t.role}</div>
                </div>
              </div>
              <p className="text-[#0a2a1f]/70 text-sm leading-relaxed italic">&ldquo;{t.quote}&rdquo;</p>
              <div className="flex gap-0.5 mt-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#f59e0b] text-xs"></i>
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
